import React from 'react';
import { motion } from 'framer-motion';
import { 
  CheckCircle2, Code2, Handshake, HeadphonesIcon, 
  LineChart, ShieldCheck 
} from 'lucide-react';

const WhyChoose = () => {
  const reasons = [
    {
      icon: <LineChart className="w-12 h-12 text-svnate-lightTeal mb-4" />,
      title: 'Business-First Approach',
      description: 'We focus on solving real business problems, not just writing code. Every architecture decision and UI flow is engineered to drive growth, boost conversions, and maximize your ROI.',
      className: 'md:col-span-2 md:row-span-2 bg-gradient-to-br from-[#0f1419] to-svnate-teal/10 flex-col justify-center',
      titleClass: 'text-3xl md:text-5xl font-black mb-6',
      iconClass: 'p-4 bg-white/5 rounded-2xl self-start mb-8'
    },
    {
      icon: <Code2 className="w-8 h-8 text-white" />,
      title: 'Scalable & Clean Code',
      description: 'Built with best practices and future growth in mind. Our code is maintainable, highly documented, and easy to extend.',
      className: 'md:col-span-2 md:row-span-1 bg-[#0f1419] justify-center',
      titleClass: 'text-2xl font-bold mb-3',
      iconClass: 'p-3 bg-white/5 rounded-xl self-start mb-4'
    },
    {
      icon: <Handshake className="w-6 h-6 text-white" />,
      title: 'Transparent Process',
      description: 'Clear communication and full visibility into project progress. No surprises.',
      className: 'md:col-span-1 md:row-span-1 bg-[#0f1419]',
      titleClass: 'text-xl font-bold mb-2',
      iconClass: 'p-2 bg-white/5 rounded-lg self-start mb-4'
    },
    {
      icon: <HeadphonesIcon className="w-6 h-6 text-white" />,
      title: 'Long-Term Support',
      description: 'Ongoing maintenance, updates, and dedicated technical support.',
      className: 'md:col-span-1 md:row-span-1 bg-[#0f1419]',
      titleClass: 'text-xl font-bold mb-2',
      iconClass: 'p-2 bg-white/5 rounded-lg self-start mb-4'
    },
    {
      icon: <ShieldCheck className="w-8 h-8 text-white" />,
      title: 'Quality Assurance',
      description: 'Rigorous testing at every single stage. We deliver bug-free, high-performance solutions that exceed expectations.',
      className: 'md:col-span-2 md:row-span-1 bg-gradient-to-bl from-[#0f1419] to-svnate-blue/10 justify-center',
      titleClass: 'text-2xl font-bold mb-3',
      iconClass: 'p-3 bg-white/5 rounded-xl self-start mb-4'
    },
    {
      icon: <CheckCircle2 className="w-8 h-8 text-white" />,
      title: 'On-Time Delivery',
      description: 'We respect your deadlines and deliver on our commitments perfectly. Your launch timeline is our absolute priority.',
      className: 'md:col-span-2 md:row-span-1 bg-[#0f1419] justify-center',
      titleClass: 'text-2xl font-bold mb-3',
      iconClass: 'p-3 bg-white/5 rounded-xl self-start mb-4'
    }
  ];

  return (
    <section className="bg-svnate-darkNavy py-24 md:py-32 px-6 md:px-12 relative" id="why-choose">
      <div className="container mx-auto relative z-10 max-w-7xl">
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="mb-16 md:mb-24 text-center"
        >
          <div className="inline-flex items-center gap-2 mb-6 px-4 py-1.5 rounded-full border border-white/10 bg-[#0f1419]">
            <span className="text-svnate-lightTeal font-semibold text-sm">Why Choose Us</span>
          </div>
          <h2 className="text-4xl md:text-6xl font-bold tracking-tight text-white mb-6">
            The SVNATE Advantage
          </h2>
          <p className="text-lg md:text-xl text-foreground/70 max-w-2xl mx-auto font-serif">
            We're not just developers — we're your partners in digital transformation, 
            dedicated to building systems that guarantee your long-term success.
          </p>
        </motion.div>

        {/* Bento Box Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 md:grid-rows-3 gap-4 md:gap-6">
          {reasons.map((reason, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              whileInView={{ opacity: 1, scale: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.5, ease: "easeOut" }}
              className={`border border-white/10 rounded-3xl p-8 hover:border-white/20 transition-all duration-500 flex flex-col group overflow-hidden relative ${reason.className}`}
            >
              {/* Subtle hover glow effect */}
              <div className="absolute inset-0 bg-white/0 group-hover:bg-white/[0.02] transition-colors duration-500 pointer-events-none" />
              
              <div className="relative z-10 flex flex-col h-full">
                <div className={`${reason.iconClass} group-hover:scale-110 group-hover:rotate-3 transition-transform duration-500`}>
                  {reason.icon}
                </div>
                <h3 className={`${reason.titleClass} text-white group-hover:text-svnate-lightTeal transition-colors duration-300`}>
                  {reason.title}
                </h3>
                <p className="text-base font-medium text-foreground/70 leading-relaxed font-serif">
                  {reason.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyChoose;
